// Shared ECharts lifecycle: SVG renderer, resize handling, and a re-init when the
// colour mode flips (the dark palette is a SELECTED set of steps, not an auto-flip,
// so the chart is rebuilt against the mode's own tokens rather than filtered).
import { useEffect, useRef } from 'react';
// Tree-shaken: only the chart types, components and renderer this dashboard uses.
// The full `echarts` barrel is ~1.3 MB; this is a fraction of it.
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';

echarts.use([LineChart, BarChart, GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent, SVGRenderer]);

export function useChart(buildOption, deps, { mode, onInit = null }) {
  const ref = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = echarts.init(ref.current, null, { renderer: 'svg' });
    chartRef.current = chart;
    chart.setOption(buildOption());
    if (onInit) onInit(chart);

    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, [...deps, mode]);

  // Exposed so a legend can drive the chart (hover a key → the segment lifts).
  return [ref, chartRef];
}

/** Highlight/downplay a series from outside the canvas. */
export function focusSeries(chartRef, seriesName, on) {
  const chart = chartRef.current;
  if (!chart) return;
  chart.dispatchAction({ type: on ? 'highlight' : 'downplay', seriesName });
}

/** Tooltip chrome shared by every chart, themed from the mode's own tokens. */
export function tooltipStyle(c) {
  return {
    backgroundColor: c.surface,
    borderColor: c.axis,
    borderWidth: 1,
    padding: [8, 10],
    textStyle: { color: c.textPrimary, fontSize: 12 },
    extraCssText: 'box-shadow: 0 2px 10px rgba(0,0,0,0.12); border-radius: 6px;',
  };
}

/** Recessive axis + hairline solid grid (never dashed). */
export function axisStyle(c) {
  return {
    axisLine: { lineStyle: { color: c.axis, width: 1 } },
    axisTick: { show: false },
    axisLabel: { color: c.muted, fontSize: 11 },
    splitLine: { show: true, lineStyle: { color: c.grid, width: 1, type: 'solid' } },
  };
}
